import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Shield, Lock, Eye, UserCheck, Mail, ArrowLeft } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteSettings';

const PrivacyPolicy = () => {
  const { contactEmail } = useSiteSettings();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-md py-4' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/assets/logos/Phoelynx_Transparent.webp"
              alt="Phoelynx France"
              className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:brightness-110 drop-shadow-lg group-hover:drop-shadow-xl"
            />
            <span className="lg:text-2xl md:text-xl sm:text-lg font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">Phoelynx France</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="hover:text-red-400 transition-colors">ACCUEIL</Link>
            <Link to="/#services" className="hover:text-red-400 transition-colors">SERVICES</Link>
            <Link to="/projets" className="hover:text-red-400 transition-colors">PROJETS</Link>
            <Link to="/#contact" className="hover:text-red-400 transition-colors">CONTACT</Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden">
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-md">
            <div className="flex flex-col p-6 space-y-4">
              <Link to="/" className="hover:text-red-400 transition-colors">ACCUEIL</Link>
              <Link to="/#services" className="hover:text-red-400 transition-colors">SERVICES</Link>
              <Link to="/projets" className="hover:text-red-400 transition-colors">PROJETS</Link>
              <Link to="/#contact" className="hover:text-red-400 transition-colors">CONTACT</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Header */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-red-600/20 to-sky-600/20">
              <Shield className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">Politique de Confidentialité</h1>
          </div>

          <p className="text-gray-400 text-lg">
            Dernière mise à jour : 1er janvier 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-invert prose-red max-w-none">

            {/* Introduction */}
            <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Eye className="w-6 h-6 text-red-400" />
                Introduction
              </h2>
              <p className="text-gray-300 leading-relaxed">
                Phoelynx France respecte votre vie privée et s'engage à protéger vos données personnelles.
                Cette politique de confidentialité vous informe sur la façon dont nous collectons,
                utilisons et protégeons vos informations personnelles lorsque vous utilisez notre site web.
              </p>
            </div>

            {/* Données collectées */}
            <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <UserCheck className="w-6 h-6 text-red-400" />
                Données collectées
              </h2>
              <div className="text-gray-300 space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Données personnelles</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Nom et prénom (formulaires de contact)</li>
                    <li>Adresse email (demandes de devis, newsletter)</li>
                    <li>Numéro de téléphone (prise de contact)</li>
                    <li>Entreprise/organisation (contexte professionnel)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Données techniques</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Adresse IP</li>
                    <li>Type de navigateur et version</li>
                    <li>Pages visitées et durée de visite</li>
                    <li>Référent (site depuis lequel vous arrivez)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Utilisation des données */}
            <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Lock className="w-6 h-6 text-red-400" />
                Utilisation des données
              </h2>
              <div className="text-gray-300 space-y-4">
                <p>Vos données personnelles sont utilisées pour :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Répondre à vos demandes de contact et devis</li>
                  <li>Vous envoyer des informations sur nos services (avec votre consentement)</li>
                  <li>Améliorer notre site web et nos services</li>
                  <li>Respecter nos obligations légales</li>
                  <li>Analyser l'utilisation du site pour optimiser l'expérience utilisateur</li>
                </ul>
              </div>
            </div>

            {/* Cookies */}
            <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
              <h2 className="text-2xl font-bold mb-4">Cookies et technologies similaires</h2>
              <div className="text-gray-300 space-y-4">
                <p>Notre site utilise des cookies pour :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Cookies fonctionnels :</strong> Nécessaires au bon fonctionnement du site</li>
                  <li><strong>Cookies analytiques :</strong> Pour comprendre l'utilisation du site (anonymisés)</li>
                  <li><strong>Cookies de préférences :</strong> Pour mémoriser vos choix (langue, etc.)</li>
                </ul>
                <p className="text-sm bg-red-900/20 p-3 rounded-lg">
                  Vous pouvez gérer vos préférences de cookies via les paramètres de votre navigateur.
                </p>
              </div>
            </div>

            {/* Vos droits RGPD */}
            <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
              <h2 className="text-2xl font-bold mb-4">Vos droits (RGPD)</h2>
              <div className="text-gray-300 space-y-4">
                <p>Conformément au RGPD, vous disposez des droits suivants :</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-black/50 p-4 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Droit d'accès</h4>
                    <p className="text-sm">Connaître les données que nous détenons sur vous</p>
                  </div>
                  <div className="bg-black/50 p-4 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Droit de rectification</h4>
                    <p className="text-sm">Corriger vos données inexactes</p>
                  </div>
                  <div className="bg-black/50 p-4 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Droit à l'effacement</h4>
                    <p className="text-sm">Supprimer vos données personnelles</p>
                  </div>
                  <div className="bg-black/50 p-4 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Droit d'opposition</h4>
                    <p className="text-sm">Vous opposer au traitement</p>
                  </div>
                  <div className="bg-black/50 p-4 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Droit à la portabilité</h4>
                    <p className="text-sm">Récupérer vos données</p>
                  </div>
                  <div className="bg-black/50 p-4 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Droit de limitation</h4>
                    <p className="text-sm">Limiter le traitement</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sécurité */}
            <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
              <h2 className="text-2xl font-bold mb-4">Sécurité des données</h2>
              <div className="text-gray-300 space-y-4">
                <p>Nous mettons en place des mesures techniques et organisationnelles appropriées pour protéger vos données :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Chiffrement des données en transit (HTTPS)</li>
                  <li>Accès restreint aux données personnelles</li>
                  <li>Sauvegardes sécurisées</li>
                  <li>Formation du personnel à la protection des données</li>
                </ul>
              </div>
            </div>

            {/* Contact */}
            <div className="mb-12 p-6 border border-red-500/30 rounded-xl bg-red-900/10">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Mail className="w-6 h-6 text-red-400" />
                Contact et exercice de vos droits
              </h2>
              <div className="text-gray-300 space-y-4">
                <p>Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits :</p>
                <div className="bg-black/50 p-4 rounded-lg">
                  <p><strong>Email :</strong> {contactEmail}</p>
                  <p><strong>Adresse :</strong> Saint-Nexans, Dordogne, France</p>
                </div>
                <p className="text-sm">
                  Nous nous engageons à répondre à vos demandes dans un délai de 30 jours maximum.
                </p>
              </div>
            </div>

            {/* Modifications */}
            <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
              <h2 className="text-2xl font-bold mb-4">Modifications de cette politique</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  Cette politique de confidentialité peut être mise à jour occasionnellement.
                  Nous vous informerons de tout changement significatif en publiant la nouvelle
                  politique sur cette page avec une date de "dernière mise à jour" révisée.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-3 group">
              <img
                src="/assets/logos/Phoelynx_Transparent.webp"
                alt="Phoelynx France"
                className="w-10 h-10 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:brightness-110 drop-shadow-lg group-hover:drop-shadow-xl"
              />
              <div>
                <span className="text-lg font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">PHOELYNX FRANCE</span>
                <p className="text-xs text-gray-400">Micro-entreprise • SIREN 991540220</p>
              </div>
            </div>

            <div className="flex flex-col items-center md:items-end gap-2">
              <p className="text-gray-400 text-sm">© 2025-{currentYear} Phoelynx France. Tous droits réservés.</p>
              <div className="flex gap-4 text-xs">
                <Link to="/mentions-legales" className="text-gray-500 hover:text-red-400 transition-colors">
                  Mentions légales
                </Link>
                <span className="text-red-400">Confidentialité</span>
                <Link to="/conditions-utilisation" className="text-gray-500 hover:text-red-400 transition-colors">
                  CGU
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;